import { useSafeWorkflow } from '../workflow/hooks';
import { ClientError } from 'graphql-request';

export type ErrorCode = 'UNAUTHORIZED' | string;

const getErrorCode = (error: unknown): ErrorCode | undefined => {
  if (error instanceof ClientError) {
    const firstError = error.response.errors?.[0];
    if (firstError?.extensions?.code) {
      return firstError.extensions.code as ErrorCode;
    }
  }

  return undefined;
};

/**
 * Extracts a user-facing error message from a GraphQL error response.
 * Only returns a message if the server explicitly provided a userFacingMessage
 * extension, indicating the message is safe to display to the user.
 * Returns undefined for internal error messages.
 */
export const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof ClientError) {
    const firstError = error.response.errors?.[0];
    const userFacingMessage = firstError?.extensions?.userFacingMessage;
    if (typeof userFacingMessage === 'string' && userFacingMessage.trim()) {
      return userFacingMessage;
    }
  }
  return undefined;
};

export const useErrorHandler = () => {
  const workflow = useSafeWorkflow();

  return (error?: unknown): void => {
    if (!error) return;
    const code = getErrorCode(error);

    switch (code) {
      case 'UNAUTHORIZED':
        if (!workflow?.requestId) {
          workflow?.logout();
        }
        return;

      default:
        return;
    }
  };
};
